/**
 * Calls to the escrow from the connected wallet. A creator claims a payout, and a brand cancels
 * its campaign before the start or withdraws its refund (specification §5.3). The admin team
 * settles a campaign from Morcast's settler wallet, voids a deposit from its owner wallet, and
 * withdraws a settled campaign's fee, which the escrow sends to Morcast's treasury whoever asks
 * (§5.4, §5.5, §10, D28).
 *
 * Each call is simulated before the wallet is asked, so that a refusal by the escrow is explained
 * instead of costing gas, then sent and awaited until it is mined.
 */

import { morcastEscrowAbi } from "@morcast/protocol";
import {
  type Address,
  BaseError,
  ContractFunctionRevertedError,
  type Hex,
  UserRejectedRequestError,
} from "viem";
import type { Config } from "wagmi";
import {
  getConnection,
  simulateContract,
  switchChain,
  waitForTransactionReceipt,
  writeContract,
} from "wagmi/actions";

/** The escrow calls creators, brands and the admin team make from the web app. */
export type EscrowCall =
  | {
      functionName: "claim";
      args: readonly [id: bigint, wallet: Address, amount: bigint, proof: readonly Hex[]];
    }
  | { functionName: "cancel"; args: readonly [id: bigint] }
  | { functionName: "withdrawBrand"; args: readonly [id: bigint] }
  | {
      functionName: "settle";
      args: readonly [id: bigint, recognized: bigint, merkleRoot: Hex, resultHash: Hex];
    }
  | { functionName: "voidCampaign"; args: readonly [id: bigint] }
  | { functionName: "withdrawFee"; args: readonly [id: bigint] };

/** A call that cannot go ahead. The message is for the user. */
export class EscrowCallError extends Error {
  override name = "EscrowCallError";
}

/**
 * Sends a call to the escrow from the connected wallet and waits until it is mined.
 *
 * @returns The transaction hash.
 * @throws EscrowCallError, or a wallet or chain error. `describeEscrowError` explains either.
 */
export async function callEscrow(
  config: Config,
  target: { chainId: number; escrow: Address },
  call: EscrowCall,
): Promise<Hex> {
  const { address: account, chainId } = getConnection(config);
  if (!account) {
    throw new EscrowCallError("Connect your wallet first.");
  }
  if (chainId !== target.chainId) {
    await switchChain(config, { chainId: target.chainId });
  }
  const { request } = await simulateContract(config, {
    chainId: target.chainId,
    account,
    address: target.escrow,
    abi: morcastEscrowAbi,
    ...call,
  } as Parameters<typeof simulateContract>[1]);
  const hash = await writeContract(config, request);
  const receipt = await waitForTransactionReceipt(config, { chainId: target.chainId, hash });
  if (receipt.status !== "success") {
    throw new EscrowCallError(`Transaction ${hash} failed on-chain.`);
  }
  return hash;
}

/** Messages for the escrow's refusals that creators, brands and the admin team can meet. */
const REFUSALS: Record<string, string> = {
  AlreadyClaimed: "This payout is claimed already.",
  InvalidProof: "The escrow does not recognise this payout.",
  PoolExceeded: "The escrow's creator pool cannot cover this payout.",
  CampaignStarted: "The campaign has started, so it can no longer be cancelled.",
  NotBrand: "Only the wallet that deposited the budget can do this.",
  InvalidStatus: "The campaign's status no longer allows this.",
  AlreadyWithdrawn: "The refund is withdrawn already.",
  RefundNotAvailable:
    "The budget can be withdrawn only from Day 10, if the campaign is not settled.",
  NotSettler: "Only the escrow's settler wallet can send a settlement.",
  OutsideSettlementWindow: "The escrow takes a settlement only from Day 5 until Day 10.",
  OwnableUnauthorizedAccount: "Only the escrow's owner wallet can do this.",
};

/** The message for the user about a call that failed. */
export function describeEscrowError(error: unknown): string {
  if (error instanceof EscrowCallError) {
    return error.message;
  }
  if (error instanceof BaseError) {
    if (error.walk((cause) => cause instanceof UserRejectedRequestError)) {
      return "You declined in your wallet, so nothing was sent.";
    }
    const revert = error.walk((cause) => cause instanceof ContractFunctionRevertedError);
    const refusal =
      revert instanceof ContractFunctionRevertedError
        ? REFUSALS[revert.data?.errorName ?? ""]
        : undefined;
    return refusal ?? `The transaction failed. ${error.shortMessage}`;
  }
  return "The transaction failed. Please try again.";
}
